import React from "react";
import { LineItemOrderObject } from "../../../types";

interface CatOrderToolbarProps {
	catOrderMode: boolean;
	isLoadingCatMappings: boolean;
	isLineItemOrderDefinitionsLoading: boolean;
	isLineItemOrderDefinitionsUpdating: boolean;
	lineItemOrderObjects: LineItemOrderObject[];
	hasExistingDefinition: boolean;
	onOrderByCat: () => void;
	onDeleteClick: () => void;
}

const CatOrderToolbar: React.FC<CatOrderToolbarProps> = ({
	catOrderMode,
	isLoadingCatMappings,
	isLineItemOrderDefinitionsLoading,
	isLineItemOrderDefinitionsUpdating,
	lineItemOrderObjects,
	hasExistingDefinition,
	onOrderByCat,
	onDeleteClick,
}) => {
	if (catOrderMode) {
		return (
			<div className="flex items-center gap-2 mb-8 px-4 py-3 bg-indigo-50 border border-indigo-200 rounded-md text-sm text-indigo-700">
				<svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
				</svg>
				Drag and drop line items or entire CAT groupings until you have a desired order, then click{" "}
				<span className="font-semibold mx-1">"Apply Order"</span> to save. After you click Apply Order, the CAT grouping headers will go away.
			</div>
		);
	}

	return (
		<div className="flex items-center gap-3 mb-8">
			<div className="relative group inline-block">
				<button
					onClick={onOrderByCat}
					disabled={isLoadingCatMappings || isLineItemOrderDefinitionsLoading || lineItemOrderObjects.length > 0}
					className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
				>
					{isLoadingCatMappings ? (
						<>
							<svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
								<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
								<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
							</svg>
							Loading mappings...
						</>
					) : (
						<>
							<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h8M4 18h8" />
							</svg>
							Order by CAT codes
						</>
					)}
				</button>
				{lineItemOrderObjects.length > 0 && (
					<div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block w-64 px-3 py-2 bg-gray-800 text-white text-xs rounded-md shadow-lg text-center pointer-events-none z-10">
						Delete line items in this order to start over ordering by CAT
						<div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
					</div>
				)}
			</div>
			{lineItemOrderObjects.length > 0 && hasExistingDefinition && (
				<button
					onClick={onDeleteClick}
					disabled={isLineItemOrderDefinitionsUpdating}
					title="Delete all line items from this order"
					className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
				>
					<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
					</svg>
				</button>
			)}
		</div>
	);
};

export default CatOrderToolbar;
